/**
 * PostDetailsModal Component
 *
 * Modal to display full details of a post from the calendar
 */

import PropTypes from "prop-types";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

function PostDetailsModal({ open, onClose, post }) {
  if (!post) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case "Posted":
        return "success";
      case "Pending Approval":
      case "Good to go":
        return "warning";
      case "Rejected":
        return "error";
      default:
        return "info";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const platforms = [];
  if (post.linkedin_post_url || post.linkedinCopy) platforms.push("LinkedIn");
  if (post.x_post_url || post.xCopy) platforms.push("X");
  if (post.threads_post_url || post.threadsCopy) platforms.push("Threads");
  if (post.instagramCopy) platforms.push("Instagram");

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Card
        sx={{
          position: "relative",
          maxWidth: 800,
          width: "90%",
          maxHeight: "90vh",
          overflow: "auto",
          p: 3,
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
          }}
        >
          <Icon>close</Icon>
        </IconButton>

        {/* Header */}
        <MDBox mb={2}>
          <MDBox display="flex" alignItems="center" gap={1} mb={1}>
            <MDTypography variant="h4" fontWeight="bold">
              {post.title || "Untitled Post"}
            </MDTypography>
            <MDBadge badgeContent={post.status} color={getStatusColor(post.status)} />
          </MDBox>

          <MDBox display="flex" gap={1} flexWrap="wrap">
            {platforms.map((platform) => (
              <Chip
                key={platform}
                label={platform}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </MDBox>
        </MDBox>

        <Divider />

        {/* Post Details */}
        <MDBox mt={2}>
          {/* Date */}
          <MDBox mb={2}>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Created: {formatDate(post.created_at || post.date)}
            </MDTypography>
          </MDBox>

          {/* Abstract */}
          {post.abstract && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="info">
                Abstract
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {post.abstract}
              </MDTypography>
            </MDBox>
          )}

          {/* Content Preview */}
          {post.content && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="info">
                Content Preview
              </MDTypography>
              <MDBox
                sx={{
                  maxHeight: 200,
                  overflow: "auto",
                  p: 1.5,
                  backgroundColor: ({ palette }) => palette.grey[100],
                  borderRadius: "8px",
                }}
              >
                <MDTypography variant="caption" color="text" component="pre" sx={{ margin: 0 }}>
                  {post.content.substring(0, 500)}
                  {post.content.length > 500 ? "..." : ""}
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          {/* Social Media Copies */}
          {post.linkedinCopy && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="info">
                LinkedIn Copy
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {post.linkedinCopy}
              </MDTypography>
            </MDBox>
          )}

          {post.xCopy && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="info">
                X Copy
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {post.xCopy}
              </MDTypography>
            </MDBox>
          )}

          {post.threadsCopy && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="info">
                Threads Copy
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {post.threadsCopy}
              </MDTypography>
            </MDBox>
          )}

          {post.instagramCopy && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="info">
                Instagram Copy
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {post.instagramCopy}
              </MDTypography>
            </MDBox>
          )}

          {/* Posted URLs */}
          {post.status === "Posted" && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="success" mb={1}>
                Published Links
              </MDTypography>
              <MDBox display="flex" flexDirection="column" gap={1}>
                {post.blog_url && (
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    href={post.blog_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon sx={{ mr: 1 }}>link</Icon>
                    View Blog Post
                  </MDButton>
                )}
                {post.linkedin_post_url && (
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    href={post.linkedin_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn Post
                  </MDButton>
                )}
                {post.x_post_url && (
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    href={post.x_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    X Post
                  </MDButton>
                )}
                {post.threads_post_url && (
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    href={post.threads_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Threads Post
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          )}

          {/* Source Info */}
          {post.sourceHeadline && (
            <MDBox mb={2}>
              <MDTypography variant="button" fontWeight="bold" color="text">
                Source
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                {post.sourceHeadline}
              </MDTypography>
              {post.sourceURL && (
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  href={post.sourceURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ pl: 0 }}
                >
                  View Source
                </MDButton>
              )}
            </MDBox>
          )}
        </MDBox>

        {/* Footer Actions */}
        <MDBox mt={3} display="flex" justifyContent="flex-end">
          <MDButton variant="gradient" color="dark" onClick={onClose}>
            Close
          </MDButton>
        </MDBox>
      </Card>
    </Modal>
  );
}

PostDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  post: PropTypes.shape({
    title: PropTypes.string,
    abstract: PropTypes.string,
    content: PropTypes.string,
    status: PropTypes.string,
    linkedinCopy: PropTypes.string,
    xCopy: PropTypes.string,
    threadsCopy: PropTypes.string,
    instagramCopy: PropTypes.string,
    linkedin_post_url: PropTypes.string,
    x_post_url: PropTypes.string,
    threads_post_url: PropTypes.string,
    blog_url: PropTypes.string,
    sourceHeadline: PropTypes.string,
    sourceURL: PropTypes.string,
    created_at: PropTypes.string,
    date: PropTypes.string,
  }),
};

export default PostDetailsModal;
